import { useEffect, useState } from "react";
import { MailCheck, LogOut, ShieldCheck, MonitorSmartphone, History } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Btn, Card, Field, Input, PageHeader } from "../components/common";
import ChangePassword from "./ChangePassword";

const dt = (s) => (s || "").replace("T", " ").slice(0, 16);

export function AddEmail() {
  const { user, logout } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/account/email", { email: email.trim(), origin: window.location.origin }); setMsg(data.message); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="max-w-lg mx-auto space-y-4" data-testid="add-email-page">
      <MailCheck className="h-10 w-10 text-[#5C4033]" />
      <h1 className="font-serif text-2xl font-bold">Add and verify your email address</h1>
      <p>Welcome, {user.name}. GKK-FMIS now uses email sign-in. Please enter your personal email address; we will send a link to confirm it belongs to you. After verifying, sign in with this email and your current password.</p>
      <Field label="Your email address"><Input type="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="add-email-input" /></Field>
      {msg && <p className="rounded-xl bg-[#E8F5E9] p-3 font-semibold" data-testid="add-email-message">{msg}</p>}
      <Btn className="w-full" loading={busy} disabled={busy || !email.includes("@")} onClick={send} data-testid="add-email-submit">Send verification link</Btn>
      <Btn v="ghost" className="w-full" onClick={logout} data-testid="add-email-logout"><LogOut className="h-5 w-5" /> Sign out</Btn>
    </Card>
  );
}

function Sessions({ a, reload }) {
  const out = async () => {
    try { const { data } = await api.post("/account/sessions/revoke-others"); toast.success(`${data.revoked} other session(s) signed out`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card data-testid="account-sessions">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-3"><MonitorSmartphone className="h-5 w-5" /> Active sessions and devices</h2>
      {a.sessions.map((s) => <div key={s.id} className="border-b border-[#F3EDE2] py-2 text-sm"><b>{s.current ? "This device" : "Other device"}</b> · {s.method} · signed in {dt(s.created_at)} · last active {dt(s.last_seen)}<div className="text-xs text-[#6E6259] truncate">{s.ua}</div></div>)}
      <Btn v="outline" className="mt-3" onClick={out} data-testid="revoke-others-button">Sign out all other sessions</Btn>
    </Card>
  );
}

export default function MyAccount() {
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const load = () => api.get("/account/activity").then((r) => setA(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-5">
      <PageHeader title="My Account" sub={`${user.name} · ${user.position || user.role} · ${user.email || user.username}`} />
      <Card data-testid="security-settings"><h2 className="font-bold text-lg flex items-center gap-2 mb-1"><ShieldCheck className="h-5 w-5" /> Security Settings</h2>
        {(user.auth_methods || []).includes("google") && <p className="text-sm text-[#6E6259]">Google Sign-In is linked to this account. GKK-FMIS cannot change your Google password.</p>}
        {a && <p className="text-sm text-[#6E6259]">Password last changed: {dt(a.password_changed_at) || "not recorded"}</p>}</Card>
      <ChangePassword />
      {a && <>
        <Sessions a={a} reload={load} />
        <Card data-testid="account-logins"><h2 className="font-bold text-lg flex items-center gap-2 mb-3"><History className="h-5 w-5" /> My Account Activity — recent sign-ins</h2>
          {a.logins.map((l) => <div key={l.id} className={`text-sm py-1 ${l.success ? "" : "text-[#B91C1C]"}`}>{dt(l.at)} · {l.success ? "Signed in" : "Failed attempt"} · {l.method}</div>)}</Card>
        <Card data-testid="account-actions"><h2 className="font-bold text-lg mb-3">Submissions, attestations and verifications</h2>
          {a.actions.length ? a.actions.map((x) => <div key={x.id} className="text-sm py-1 border-b border-[#F3EDE2]">{dt(x.at)} · <b>{x.action.replaceAll("_", " ")}</b> · {x.entity} {x.details?.ref_no || ""}</div>) : <p className="text-sm text-[#6E6259]">No actions yet.</p>}</Card>
        {a.security.length > 0 && <Card><h2 className="font-bold text-lg mb-3">Security notifications</h2>{a.security.map((n) => <p key={n.id} className="text-sm py-1">{dt(n.created_at)} · <b>{n.title}</b> — {n.body}</p>)}</Card>}
      </>}
    </div>
  );
}
